/**
 * Free address/pincode lookup using India Post (pincode → location) and Nominatim (address → pincode).
 * No API keys required.
 */

const INDIA_POST_BASE = "https://api.postalpincode.in/pincode";
const NOMINATIM_BASE = "https://nominatim.openstreetmap.org/search";
const NOMINATIM_USER_AGENT = "CarePro/1.0 (Contact form address lookup)";

export interface LocationFromPincode {
  state: string;
  country: string;
  city: string;
  address: string;
}

interface IndiaPostPostOffice {
  Name: string;
  District: string;
  State: string;
  Country: string;
  Block?: string;
}

interface IndiaPostResponseItem {
  Status: string;
  PostOffice: IndiaPostPostOffice[] | null;
}

/**
 * Fetch location (state, country, city, address) from a 6-digit Indian pincode.
 * Uses India Post API (free, no key).
 */
export async function fetchLocationByPincode(
  pincode: string
): Promise<LocationFromPincode | null> {
  const trimmed = (pincode || "").trim();
  if (!/^\d{6}$/.test(trimmed)) return null;

  try {
    const res = await fetch(`${INDIA_POST_BASE}/${trimmed}`);
    if (!res.ok) return null;
    const data: IndiaPostResponseItem[] = await res.json();
    const first = data?.[0];
    if (
      !first ||
      first.Status !== "Success" ||
      !first.PostOffice ||
      first.PostOffice.length === 0
    )
      return null;

    const office = first.PostOffice[0];
    const state = office.State?.trim() ?? "";
    const country = office.Country?.trim() ?? "";
    const city = office.District?.trim() ?? "";
    const address =
      [office.Block?.trim(), office.Name?.trim()].filter(Boolean).join(", ") ||
      office.Name?.trim() ||
      city ||
      "";

    return { state, country, city, address };
  } catch {
    return null;
  }
}

/**
 * Fetch pincode from address + state + country.
 * Uses Nominatim (OSM); free, no key. Respect 1 request/second (caller should debounce).
 */
export async function fetchPincodeByAddress(
  address: string,
  state: string,
  country: string
): Promise<string | null> {
  const parts = [address, state, country].map((s) => (s || "").trim()).filter(Boolean);
  if (parts.length === 0) return null;

  const q = parts.join(", ");
  try {
    const url = `${NOMINATIM_BASE}?q=${encodeURIComponent(q)}&format=json&limit=1`;
    const res = await fetch(url, {
      headers: { "User-Agent": NOMINATIM_USER_AGENT },
    });
    if (!res.ok) return null;
    const data: Array<{ address?: { postcode?: string; pin_code?: string }; postcode?: string }> =
      await res.json();
    const first = data?.[0];
    if (!first) return null;

    const postcode =
      first.address?.pin_code ??
      first.address?.postcode ??
      (first as { postcode?: string }).postcode;
    if (postcode == null) return null;

    const str = String(postcode).trim();
    const match = str.match(/\d{6}/);
    return match ? match[0] : (str.slice(0, 6) || null);
  } catch {
    return null;
  }
}
